/**
 * Layer 2 — Events. What happened, timestamped, structured, never prose
 * (§11). Cheap to store forever, queryable, the source the nightly digest is
 * generated from.
 *
 * This module only writes and reads the events list. Reactions to an event
 * (witness impressions, grievances) live in `sim/events.ts`, so the layer
 * stays a plain ledger.
 */

import { mintEventId } from '../../sim/ids.js';
import { compareTime } from '../../sim/time.js';
import type { Actor, EntityRef, Event, EventId, EventType, WorldState, WorldTime } from '../../sim/types/index.js';

/** An event before the sim has stamped an id and a time on it. */
export type EventDraft = Omit<Event, 'id' | 'at'>;

const EVENT_TYPES: ReadonlySet<string> = new Set<EventType>([
  'conversation',
  'transfer',
  'robbery',
  'fistfight',
  'shakedown',
  'hit',
  'gunfight',
  'interrogation',
  'appointment_kept',
  'appointment_missed',
  'obligation_lapsed',
  'opening_failed',
  'lie_discovered',
  'secret_exposed',
  'death',
  'arrest',
  'departure',
]);

export function isEventType(value: unknown): value is EventType {
  return typeof value === 'string' && EVENT_TYPES.has(value);
}

/** §17's table plus robbery: the acts that always write a permanent memory. */
export const VIOLENT_EVENT_TYPES: ReadonlySet<EventType> = new Set<EventType>([
  'fistfight',
  'shakedown',
  'robbery',
  'hit',
  'gunfight',
  'interrogation',
]);

export function isViolent(type: EventType): boolean {
  return VIOLENT_EVENT_TYPES.has(type);
}

export function recordEvent(
  state: WorldState,
  draft: EventDraft,
  at: WorldTime = state.time,
): { readonly state: WorldState; readonly event: Event } {
  const minted = mintEventId(state);
  const event: Event = { ...draft, id: minted.id, at };
  return {
    event,
    state: {
      ...minted.state,
      memory: { ...minted.state.memory, events: [...minted.state.memory.events, event] },
    },
  };
}

export function findEvent(state: WorldState, id: EventId): Event | undefined {
  return state.memory.events.find((e) => e.id === id);
}

function refersTo(ref: EntityRef | null, actor: Actor): boolean {
  if (ref === null) return false;
  if (actor === 'player') return ref.kind === 'player';
  return ref.kind === 'npc' && ref.id === actor;
}

/** Did this actor take part — as actor, target or witness. */
export function involves(event: Event, actor: Actor): boolean {
  return event.actor === actor || refersTo(event.target, actor) || event.witnesses.includes(actor);
}

/** Events both took part in, newest first. */
export function eventsBetween(state: WorldState, a: Actor, b: Actor): Event[] {
  return state.memory.events
    .filter((e) => involves(e, a) && involves(e, b))
    .sort((x, y) => compareTime(y.at, x.at));
}
